import type { GameSettings } from '../core/types';

/** Procedural Web Audio synth for Aetherloom. */
export class AudioSynth {
  private ctx: AudioContext | null = null;
  private master: GainNode | null = null;
  private musicGain: GainNode | null = null;
  private sfxGain: GainNode | null = null;
  private musicTimer: number | null = null;
  private musicStep = 0;
  private cascadeLevel = 0;
  private settings: GameSettings | null = null;
  private started = false;

  async ensure(): Promise<void> {
    if (this.ctx) {
      if (this.ctx.state === 'suspended') await this.ctx.resume();
      return;
    }
    this.ctx = new AudioContext();
    this.master = this.ctx.createGain();
    this.musicGain = this.ctx.createGain();
    this.sfxGain = this.ctx.createGain();
    this.musicGain.connect(this.master);
    this.sfxGain.connect(this.master);
    this.master.connect(this.ctx.destination);
    this.applySettings(this.settings);
    this.started = true;
  }

  applySettings(s: GameSettings | null): void {
    this.settings = s;
    if (!this.master || !this.musicGain || !this.sfxGain || !s) return;
    this.master.gain.value = s.masterVolume;
    this.musicGain.gain.value = s.musicVolume;
    this.sfxGain.gain.value = s.sfxVolume;
  }

  private tone(
    freq: number,
    dur: number,
    type: OscillatorType = 'sine',
    gain = 0.12,
    dest: 'sfx' | 'music' = 'sfx'
  ): void {
    if (!this.ctx || !this.sfxGain || !this.musicGain) return;
    const t0 = this.ctx.currentTime;
    const osc = this.ctx.createOscillator();
    const g = this.ctx.createGain();
    osc.type = type;
    osc.frequency.setValueAtTime(freq, t0);
    g.gain.setValueAtTime(0.0001, t0);
    g.gain.exponentialRampToValueAtTime(gain, t0 + 0.02);
    g.gain.exponentialRampToValueAtTime(0.0001, t0 + dur);
    osc.connect(g);
    g.connect(dest === 'music' ? this.musicGain : this.sfxGain);
    osc.start(t0);
    osc.stop(t0 + dur + 0.05);
  }

  private noise(dur: number, gain = 0.06): void {
    if (!this.ctx || !this.sfxGain) return;
    const bufferSize = Math.floor(this.ctx.sampleRate * dur);
    const buffer = this.ctx.createBuffer(1, bufferSize, this.ctx.sampleRate);
    const data = buffer.getChannelData(0);
    for (let i = 0; i < bufferSize; i++) data[i] = Math.random() * 2 - 1;
    const src = this.ctx.createBufferSource();
    src.buffer = buffer;
    const g = this.ctx.createGain();
    const filter = this.ctx.createBiquadFilter();
    filter.type = 'bandpass';
    filter.frequency.value = 1200;
    g.gain.value = gain;
    g.gain.exponentialRampToValueAtTime(0.0001, this.ctx.currentTime + dur);
    src.connect(filter);
    filter.connect(g);
    g.connect(this.sfxGain);
    src.start();
  }

  select(): void {
    this.tone(520, 0.08, 'sine', 0.08);
  }

  swap(): void {
    this.tone(360, 0.07, 'triangle', 0.1);
    this.tone(480, 0.09, 'sine', 0.06);
  }

  invalid(): void {
    this.tone(180, 0.12, 'sawtooth', 0.06);
    this.tone(140, 0.15, 'square', 0.04);
  }

  match(depth = 1): void {
    const base = 440 + Math.min(depth, 8) * 40;
    this.tone(base, 0.12, 'sine', 0.1);
    this.tone(base * 1.25, 0.14, 'triangle', 0.06);
    this.cascadeLevel = depth;
  }

  cascade(depth: number): void {
    const notes = [523.25, 587.33, 659.25, 783.99, 880, 987.77];
    const n = notes[Math.min(depth, notes.length - 1)!]!;
    this.tone(n, 0.1 + depth * 0.01, 'sine', 0.08 + depth * 0.01);
  }

  specialCreate(): void {
    this.tone(660, 0.1, 'sine', 0.1);
    this.tone(880, 0.15, 'triangle', 0.08);
    this.tone(1320, 0.2, 'sine', 0.05);
  }

  specialActivate(): void {
    this.noise(0.15, 0.05);
    this.tone(220, 0.2, 'sawtooth', 0.08);
    this.tone(440, 0.25, 'triangle', 0.1);
    this.tone(880, 0.3, 'sine', 0.06);
  }

  combo(): void {
    this.specialActivate();
    this.tone(523, 0.15, 'sine', 0.1);
    this.tone(659, 0.18, 'sine', 0.09);
    this.tone(784, 0.22, 'sine', 0.08);
    this.tone(1046, 0.3, 'triangle', 0.07);
  }

  blocker(): void {
    this.noise(0.08, 0.04);
    this.tone(150, 0.1, 'square', 0.05);
  }

  objective(): void {
    this.tone(784, 0.12, 'sine', 0.1);
    this.tone(988, 0.16, 'sine', 0.08);
  }

  ui(): void {
    this.tone(640, 0.06, 'sine', 0.06);
  }

  win(): void {
    const melody = [523, 659, 784, 1046, 784, 1046, 1318];
    melody.forEach((f, i) => {
      setTimeout(() => this.tone(f, 0.22, 'triangle', 0.1), i * 90);
    });
  }

  lose(): void {
    this.tone(300, 0.25, 'triangle', 0.1);
    this.tone(240, 0.3, 'sine', 0.08);
    this.tone(180, 0.4, 'sine', 0.07);
  }

  startMusic(theme: string): void {
    this.stopMusic();
    if (!this.ctx) return;
    // Simple arpeggio loop colored by theme
    const scales: Record<string, number[]> = {
      ember: [261.63, 329.63, 392.0, 523.25],
      tide: [293.66, 349.23, 440.0, 587.33],
      gale: [329.63, 415.3, 493.88, 659.25],
      root: [246.94, 311.13, 369.99, 493.88],
      void: [277.18, 349.23, 415.3, 554.37],
    };
    const scale = scales[theme] ?? scales.ember!;
    this.musicStep = 0;
    const tick = () => {
      if (!this.ctx || !this.musicGain) return;
      const note = scale[this.musicStep % scale.length]!;
      this.tone(note, 0.35, 'sine', 0.03, 'music');
      if (this.musicStep % 4 === 0) {
        this.tone(note / 2, 0.5, 'triangle', 0.02, 'music');
      }
      this.musicStep++;
      this.musicTimer = window.setTimeout(tick, 420);
    };
    tick();
  }

  stopMusic(): void {
    if (this.musicTimer != null) {
      clearTimeout(this.musicTimer);
      this.musicTimer = null;
    }
  }
}

export const audio = new AudioSynth();
